import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ArrowLeft, Calendar, Pin, User, Trash2, Edit, AlertCircle, Megaphone } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/contexts/AuthContext'
import { UserRole } from '@/models/user.model'
import {
  useAnnouncement,
  useAnnouncementPinState,
  useDeleteAnnouncement,
  useUpdateAnnouncement,
} from '@/hooks/useAnnouncements'
import { CreateAnnouncementModal } from '@/components/CreateAnnouncementModal'
import { AnnouncementMutationPayload } from '@/services/announcementsService'
import { paths } from '@/routes/path'

export function AnnouncementDetailsPage() {
  const { announcementId } = useParams<{ announcementId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const canManage = user?.role === UserRole.ADMIN || user?.role === UserRole.INSTRUCTOR

  const { data: announcement, isLoading, error } = useAnnouncement(announcementId)
  const updateAnnouncement = useUpdateAnnouncement()
  const deleteAnnouncement = useDeleteAnnouncement()
  const pinAnnouncement = useAnnouncementPinState()

  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [isActioning, setIsActioning] = useState(false)

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-secondary-500">
        Loading announcement…
      </div>
    )
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="flex flex-col items-center gap-3 py-10 text-center text-red-700">
          <AlertCircle className="h-10 w-10" />
          <p>{(error as Error)?.message || 'Unable to load announcement.'}</p>
          <Link to={paths.app.announcements.root.absolute}>
            <Button variant="outline">Back to announcements</Button>
          </Link>
        </CardContent>
      </Card>
    )
  }

  if (!announcement) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-12 text-secondary-600">
          <Megaphone className="h-10 w-10 text-secondary-300" />
          <p>This announcement is no longer available.</p>
          <Link to={paths.app.announcements.root.absolute}>
            <Button>Go back</Button>
          </Link>
        </CardContent>
      </Card>
    )
  }

  const handleSubmit = async (payload: AnnouncementMutationPayload) => {
    if (!announcement?.id) return
    try {
      setIsActioning(true)
      await updateAnnouncement.mutateAsync({ announcementId: announcement.id, payload })
      toast.success('Announcement updated')
      setIsEditorOpen(false)
    } catch (mutationError) {
      toast.error((mutationError as Error)?.message || 'Unable to update announcement')
    } finally {
      setIsActioning(false)
    }
  }

  const handlePinToggle = async () => {
    if (!announcement.id) return
    try {
      setIsActioning(true)
      await pinAnnouncement.mutateAsync({
        announcementId: announcement.id,
        action: announcement.isPinned ? 'unpin' : 'pin',
      })
      toast.success(announcement.isPinned ? 'Announcement unpinned' : 'Announcement pinned')
    } catch (mutationError) {
      toast.error((mutationError as Error)?.message || 'Unable to update pin state')
    } finally {
      setIsActioning(false)
    }
  }

  const handleDelete = async () => {
    if (!announcement.id) return
    const confirmed = window.confirm('Delete this announcement? This cannot be undone.')
    if (!confirmed) return
    try {
      setIsActioning(true)
      await deleteAnnouncement.mutateAsync(announcement.id)
      toast.success('Announcement deleted')
      navigate(paths.app.announcements.root.absolute)
    } catch (mutationError) {
      toast.error((mutationError as Error)?.message || 'Unable to delete announcement')
    } finally {
      setIsActioning(false)
    }
  }

  const formatDate = (date?: string | null) => {
    if (!date) return ''
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 text-secondary-500">
            {announcement.isPinned && (
              <Badge className="bg-warning-100 text-warning-800">
                <Pin className="mr-1 h-3 w-3" />
                Pinned
              </Badge>
            )}
            {!announcement.isActive && <Badge variant="outline">Archived</Badge>}
          </div>
          <h1 className="mt-2 text-3xl font-bold text-secondary-900">{announcement.title}</h1>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setIsEditorOpen(true)} disabled={isActioning}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
            <Button variant="outline" onClick={handlePinToggle} disabled={isActioning}>
              <Pin className="mr-2 h-4 w-4" />
              {announcement.isPinned ? 'Unpin' : 'Pin'}
            </Button>
            <Button variant="outline" className="text-red-600 hover:bg-red-50" onClick={handleDelete} disabled={isActioning}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
          <CardDescription>Published information for learners</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-secondary-600">
          <div className="flex flex-wrap gap-4 text-sm">
            {announcement.authorName && (
              <span className="inline-flex items-center gap-2">
                <User className="h-4 w-4" />
                {announcement.authorName}
              </span>
            )}
            {announcement.createdAt && (
              <span className="inline-flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {formatDate(announcement.createdAt)}
              </span>
            )}
            {announcement.courseTitle && (
              <Badge variant="outline">Course: {announcement.courseTitle}</Badge>
            )}
            {announcement.programmeTitle && (
              <Badge variant="outline">Programme: {announcement.programmeTitle}</Badge>
            )}
          </div>
          <div className="rounded-lg border border-secondary-100 bg-secondary-50 p-4 text-secondary-800">
            <p className="whitespace-pre-wrap">{announcement.content}</p>
          </div>
          {announcement.updatedAt && (
            <p className="text-xs text-secondary-400">Last updated {formatDate(announcement.updatedAt)}</p>
          )}
        </CardContent>
      </Card>

      {canManage && (
        <CreateAnnouncementModal
          isOpen={isEditorOpen}
          onClose={() => setIsEditorOpen(false)}
          onSubmit={handleSubmit}
          isSubmitting={isActioning}
          announcement={announcement}
        />
      )}
    </div>
  )
}
